// @mui icons
import Icon from "@mui/material/Icon";
import Callback from "./layouts/callback";
import LogoutUI from "./layouts/logout";
import LoginUI from "./layouts/login";
import LandingPage from "./layouts/landing";
import OnboardingWorkspaceUI from "./layouts/onboarding";
import HowItWorksPage from "./layouts/how-it-works";
import RegisterUI from "./layouts/register";
import RegisterGoogleUI from "./layouts/register-google";
import VerifyEmailUI from "./layouts/verify-email";
import ForgotPasswordUI from "./layouts/forgot-password";
import ResetPasswordUI from "./layouts/reset-password";
import InviteUI from "./layouts/invite";
import SetupWizard from "./layouts/setup";
import WorkspaceSettingsPage from "./layouts/workspace-settings";
import Home from "./layouts/home";
import Report from "./layouts/report/index";
import Dashboards from "./layouts/dashboards";
import DashboardDetailPage from "./layouts/dashboards/ui/DashboardDetailPage";
import Widgets from "./layouts/widgets";
import WidgetEditPage from "./layouts/widgets/ui/WidgetEditPage";
import QueryEditPage from "./layouts/dashboard-queries/ui/QueryEditPage";
import Anomalies from "./layouts/anomalies";
import DefinitionEditPage from "./layouts/anomalies/ui/DefinitionEditPage";
import SuperAdminPortal from "./layouts/super-admin";
import SuperAdminIndexRedirect from "./layouts/super-admin/SuperAdminIndexRedirect";
import { SUPER_ADMIN_SECTIONS } from "./layouts/super-admin/sections";
import PublicShare from "./layouts/public-share";
import DataPrep from "./layouts/data-prep";
import DataSources from "./layouts/data-sources";
import VpnProfiles from "./layouts/admin-vpn-profiles";
import AiContext from "./layouts/ai-context";
import Logs from "./layouts/activity-logs";
import DataPrepDesign from "./layouts/data-prep/design";
import DataPrepRuns from "./layouts/data-prep/runs";
import InsightReportJobs from "./layouts/insight-report-jobs";
import AdaptorDiscovery from "./layouts/adaptor-discovery";
import SemanticModels from "./layouts/semantic-models";
import WarehouseUsage from "./layouts/warehouse-usage";
import OpenApiCatalogs from "./layouts/openapi-catalogs";
import SchemaRelations from "./layouts/schema-relations";
import DocsIndexRedirect from "./layouts/docs";
import DocsLayout from "./layouts/docs/ui/DocsLayout";
import ReleaseNotesPage from "./layouts/docs/ui/ReleaseNotesPage";
import { Navigate } from "react-router-dom";
import { REACT_APP_BASE_PATH } from "./config/runtimeConfig";
import { LEGACY_REDIRECTS } from "./navigation/legacyRedirects";

const swaggerUiUrl = `${(REACT_APP_BASE_PATH || "").replace(/\/+$/, "")}/swagger-ui/index.html`;
const SUPER = "admin";
const TENANT = "tenant_admin";
const USER = "user";
const REPORTER = "reporter";
const VIEWER = "viewer";
const WORKSPACE_ADMIN = [SUPER, TENANT];
const WORKSPACE_USER = [SUPER, TENANT, USER];
const REPORT_WRITE = [SUPER, TENANT, USER, REPORTER];
const REPORT_READ = [SUPER, TENANT, USER, REPORTER, VIEWER];
const HELP_ROLES = REPORT_READ;

const routes = [
  {
    whiteListed: true,
    menu: false,
    type: "collapse",
    name: "Landing",
    key: "landing",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/",
    component: <LandingPage />,
  },
  {
    whiteListed: true,
    menu: false,
    type: "collapse",
    name: "How it works",
    key: "how-it-works",
    route: "/how-it-works",
    component: <HowItWorksPage />,
  },
  {
    menu: true,
    type: "collapse",
    menuOrder: 0,
    key: "home",
    allowedRoles: REPORT_READ,
    route: "/home",
    name: "Home",
    icon: <Icon fontSize="small">home</Icon>,
    component: <Home />,
  },
  {
    menu: true,
    type: "collapse",
    menuOrder: 1,
    key: "report",
    allowedRoles: REPORT_READ,
    route: "/report",
    name: "Report",
    icon: <Icon fontSize="small">chat</Icon>,
    component: <Report />,
  },
  {
    menu: true,
    type: "collapse",
    key: "dashboards",
    parent: "analiz",
    allowedRoles: REPORT_READ,
    route: "/dashboards",
    name: "Dashboards",
    icon: <Icon fontSize="small">dashboard</Icon>,
    component: <Dashboards />,
  },
  {
    menu: false,
    type: "collapse",
    key: "dashboard-view",
    allowedRoles: REPORT_READ,
    route: "/dashboards/:id",
    name: "Dashboard",
    component: <DashboardDetailPage mode="view" />,
  },
  {
    menu: false,
    type: "collapse",
    key: "dashboard-edit",
    allowedRoles: REPORT_WRITE,
    route: "/dashboards/:id/edit",
    name: "Dashboard Edit",
    component: <DashboardDetailPage mode="edit" />,
  },
  {
    menu: true,
    type: "collapse",
    key: "widgets",
    parent: "analiz",
    allowedRoles: REPORT_READ,
    route: "/widgets",
    // Dashboard-query screens belong to this hub, so keep its menu item highlighted there.
    activePaths: ["/dashboard-queries"],
    name: "Widgets & Queries",
    icon: <Icon fontSize="small">widgets</Icon>,
    component: <Widgets />,
  },
  {
    menu: false,
    type: "collapse",
    key: "dashboard-query-new",
    allowedRoles: REPORT_WRITE,
    route: "/dashboard-queries/new",
    name: "New Dashboard Query",
    component: <QueryEditPage mode="create" />,
  },
  {
    menu: false,
    type: "collapse",
    key: "dashboard-query-edit",
    allowedRoles: REPORT_WRITE,
    route: "/dashboard-queries/:id/edit",
    name: "Edit Dashboard Query",
    component: <QueryEditPage mode="edit" />,
  },
  {
    menu: true,
    type: "collapse",
    key: "anomalies",
    parent: "sorgular",
    allowedRoles: WORKSPACE_USER,
    route: "/anomalies",
    name: "Anomalies",
    icon: <Icon fontSize="small">notification_important</Icon>,
    component: <Anomalies />,
  },
  {
    menu: false,
    type: "collapse",
    key: "anomaly-new",
    allowedRoles: WORKSPACE_USER,
    route: "/anomalies/new",
    name: "New Anomaly",
    component: <DefinitionEditPage mode="create" />,
  },
  {
    menu: false,
    type: "collapse",
    key: "anomaly-edit",
    allowedRoles: WORKSPACE_USER,
    route: "/anomalies/:id/edit",
    name: "Edit Anomaly",
    component: <DefinitionEditPage mode="edit" />,
  },
  {
    menu: true,
    type: "collapse",
    key: "data-prep",
    parent: "veriHatti",
    allowedRoles: WORKSPACE_USER,
    route: "/data-prep",
    name: "Data Prep",
    icon: <Icon fontSize="small">account_tree</Icon>,
    component: <DataPrep />,
  },
  {
    menu: true,
    menuOrder: 2,
    type: "collapse",
    key: "ai-context",
    allowedRoles: WORKSPACE_USER,
    route: "/ai-context",
    name: "AI Context",
    icon: <Icon fontSize="small">auto_awesome</Icon>,
    component: <AiContext />,
  },
  {
    menu: true,
    type: "collapse",
    key: "insight-report-jobs",
    parent: "sorgular",
    allowedRoles: WORKSPACE_USER,
    route: "/insight-report-jobs",
    name: "Insight Report Jobs",
    icon: <Icon fontSize="small">insights</Icon>,
    component: <InsightReportJobs />,
  },
  {
    menu: false,
    type: "collapse",
    key: "data-prep-design",
    allowedRoles: WORKSPACE_USER,
    route: "/data-prep/:id/design",
    name: "Data Prep Design",
    component: <DataPrepDesign />,
  },
  {
    menu: false,
    type: "collapse",
    key: "data-prep-runs",
    allowedRoles: WORKSPACE_USER,
    route: "/data-prep/:id/runs",
    name: "Data Prep Runs",
    component: <DataPrepRuns />,
  },
  {
    menu: true,
    type: "collapse",
    key: "data-sources",
    parent: "veriHatti",
    allowedRoles: WORKSPACE_USER,
    route: "/data-sources",
    name: "Data Sources",
    icon: <Icon fontSize="small">storage</Icon>,
    component: <DataSources />,
  },
  {
    menu: true,
    type: "collapse",
    key: "schema-relations",
    parent: "veriHatti",
    allowedRoles: WORKSPACE_USER,
    route: "/schema-relations",
    name: "Schema Relations",
    icon: <Icon fontSize="small">hub</Icon>,
    component: <SchemaRelations />,
  },
  {
    menu: true,
    type: "collapse",
    key: "adaptor-discovery",
    parent: "veriHatti",
    allowedRoles: WORKSPACE_USER,
    route: "/adaptor-discovery",
    name: "Adaptor Discovery",
    icon: <Icon fontSize="small">travel_explore</Icon>,
    component: <AdaptorDiscovery />,
  },
  {
    menu: true,
    type: "collapse",
    key: "semantic-models",
    parent: "veriHatti",
    allowedRoles: WORKSPACE_USER,
    route: "/semantic-models",
    name: "Semantic Models",
    icon: <Icon fontSize="small">functions</Icon>,
    component: <SemanticModels />,
  },
  {
    menu: true,
    type: "collapse",
    key: "warehouse-usage",
    parent: "veriHatti",
    allowedRoles: WORKSPACE_ADMIN,
    route: "/warehouse-usage",
    name: "Warehouse Usage",
    icon: <Icon fontSize="small">query_stats</Icon>,
    component: <WarehouseUsage />,
  },
  {
    menu: true,
    type: "collapse",
    key: "openapi-catalogs",
    parent: "veriHatti",
    allowedRoles: WORKSPACE_USER,
    route: "/openapi-catalogs",
    name: "OpenAPI Catalogs",
    icon: <Icon fontSize="small">api</Icon>,
    component: <OpenApiCatalogs />,
  },
  {
    menu: false,
    type: "collapse",
    key: "widget-new",
    allowedRoles: REPORT_WRITE,
    route: "/widgets/new",
    name: "New Widget",
    component: <WidgetEditPage mode="create" />,
  },
  {
    menu: false,
    type: "collapse",
    key: "widget-edit",
    allowedRoles: REPORT_WRITE,
    route: "/widgets/:id/edit",
    name: "Edit Widget",
    component: <WidgetEditPage mode="edit" />,
  },
  {
    menu: false,
    type: "collapse",
    key: "super-admin",
    allowedRoles: [SUPER],
    route: "/super-admin",
    name: "Super Admin",
    component: <SuperAdminIndexRedirect />,
  },
  {
    menu: false,
    type: "collapse",
    key: "super-admin-section",
    allowedRoles: [SUPER],
    route: "/super-admin/:section",
    name: "Super Admin",
    component: <SuperAdminPortal />,
  },
  // Flat menu links (no parent group); the page is served by `/super-admin/:section` above.
  ...SUPER_ADMIN_SECTIONS.map((section, index) => ({
    menu: true,
    type: "collapse" as const,
    key: section.routeKey,
    menuOrder: 10 + index,
    allowedRoles: [SUPER],
    requiresSaaS: section.saasOnly === true,
    route: `/super-admin/${section.key}`,
    name: section.labelKey,
    icon: section.icon,
  })),
  {
    menu: true,
    type: "collapse",
    key: "logs",
    parent: "ayarlar",
    allowedRoles: WORKSPACE_USER,
    route: "/logs",
    name: "Logs",
    icon: <Icon fontSize="small">receipt_long</Icon>,
    component: <Logs />,
  },
  {
    menu: true,
    type: "collapse",
    key: "docs",
    menuOrder: 900,
    menuBottom: true,
    allowedRoles: HELP_ROLES,
    route: "/docs",
    name: "Documentation",
    icon: <Icon fontSize="small">menu_book</Icon>,
    component: <DocsIndexRedirect />,
  },
  {
    menu: false,
    type: "collapse",
    key: "docs-section",
    allowedRoles: HELP_ROLES,
    route: "/docs/:section",
    name: "Documentation",
    component: <DocsLayout />,
  },
  {
    // Opened from Settings (configurator); not shown in the left sidenav.
    menu: false,
    type: "collapse",
    key: "docs-api",
    allowedRoles: HELP_ROLES,
    href: swaggerUiUrl,
    name: "API",
    icon: <Icon fontSize="small">api</Icon>,
  },
  {
    menu: false,
    type: "collapse",
    key: "release-notes",
    allowedRoles: HELP_ROLES,
    route: "/release-notes",
    name: "Release notes",
    icon: <Icon fontSize="small">new_releases</Icon>,
    component: <ReleaseNotesPage />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    key: "logout",
    route: "/logout",
    name: "Logout",
    icon: <Icon fontSize="small">logout</Icon>,
    component: <LogoutUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Login",
    key: "login",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/login",
    component: <LoginUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Register",
    key: "register",
    route: "/register",
    component: <RegisterUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Register with Google",
    key: "register-google",
    route: "/register/google",
    component: <RegisterGoogleUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Verify email",
    key: "verify-email",
    route: "/verify-email",
    component: <VerifyEmailUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Forgot password",
    key: "forgot-password",
    route: "/forgot-password",
    component: <ForgotPasswordUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Reset password",
    key: "reset-password",
    route: "/reset-password",
    component: <ResetPasswordUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Invite",
    key: "invite",
    route: "/invite",
    component: <InviteUI />,
  },
  {
    menu: false,
    whiteListed: true,
    type: "collapse",
    name: "Onboarding",
    key: "onboarding-workspace",
    route: "/onboarding/workspace",
    component: <OnboardingWorkspaceUI />,
  },
  {
    menu: false,
    type: "collapse",
    key: "setup",
    allowedRoles: WORKSPACE_ADMIN,
    route: "/setup",
    name: "Setup",
    component: <SetupWizard />,
  },
  {
    menu: true,
    type: "collapse",
    key: "workspace-settings",
    parent: "ayarlar",
    allowedRoles: WORKSPACE_ADMIN,
    route: "/workspace-settings",
    name: "Workspace",
    icon: <Icon fontSize="small">settings</Icon>,
    component: <WorkspaceSettingsPage />,
  },
  {
    menu: true,
    type: "collapse",
    key: "vpn-profiles",
    parent: "ayarlar",
    allowedRoles: WORKSPACE_ADMIN,
    /** Hidden from the menu unless the backend reports vpnEnabled. */
    requiresVpn: true,
    route: "/vpn-profiles",
    name: "VPN Profiles",
    icon: <Icon fontSize="small">vpn_lock</Icon>,
    component: <VpnProfiles />,
  },
  {
    whiteListed: true,
    menu: false,
    type: "collapse",
    name: "callback",
    key: "callback",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/callback",
    component: <Callback />,
  },
  {
    whiteListed: true,
    menu: false,
    type: "collapse",
    name: "Public Share",
    key: "public-share",
    route: "/s/:token",
    component: <PublicShare />,
  },
  // Old addresses of pages that became tabs of a hub page.
  ...LEGACY_REDIRECTS.map((redirect) => ({
    whiteListed: true,
    menu: false,
    type: "collapse",
    key: `legacy:${redirect.from}`,
    name: "Redirect",
    route: redirect.from,
    component: <Navigate to={redirect.to} replace />,
  })),
];

export default routes;
